import React, { useRef, useState } from 'react';
import { extractPdfText } from './pdfText';
import { invokeFn, fileToBase64, parseLocalDateTime, AI_FILE_TYPES, AI_MAX_FILE } from './ai';
import { LEADS_TEST_MODE, LEADS_AI_ENABLED, parseCrmText, toLocalInput, normalizePlate, rpcError } from './config';
import { L, Field, Chips } from './ui';

const FIELD_NAMES = {
  customer_name: 'nombre', phone: 'teléfono', email: 'email', city: 'localidad', vehicle_text: 'coche',
  vehicle_plate: 'matrícula', vehicle_ref: 'enlace del anuncio', vehicle_price: 'precio al contado', vehicle_price_financed: 'precio financiado', customer_message: 'mensaje',
  crm_lead_id: 'referencia del CRM', crm_url: 'enlace del CRM', received_at: 'hora de entrada', notes: 'notas'
};

const EMPTY = {
  customer_name: '', phone: '', email: '', city: '', vehicle_text: '', vehicle_plate: '', vehicle_ref: '',
  vehicle_price: '', vehicle_price_financed: '', customer_message: '', crm_lead_id: '', crm_url: '', notes: ''
};

// "15.990" / "15990,50" / "15 990 €" → número (o null).
function priceToNumber(v) {
  const s = String(v || '').replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

// Alta manual de un lead. Se puede pegar el texto de la ficha del CRM para rellenar los campos.
export default function NewLeadForm({ supabase, access, vehicles, columns, onCreated, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [receivedAt, setReceivedAt] = useState(() => toLocalInput(new Date()));
  const [claim, setClaim] = useState(false);
  // Presencial: lo crea un usuario en tienda. Online: solo un supervisor, como plan B mientras no entre solo desde el CRM.
  const [channel, setChannel] = useState('presencial');
  const online = channel === 'online';
  const [isTest, setIsTest] = useState(LEADS_TEST_MODE);
  const [paste, setPaste] = useState('');
  const [filled, setFilled] = useState([]);
  const [pasteMsg, setPasteMsg] = useState('');
  const [error, setError] = useState('');
  const [duplicate, setDuplicate] = useState('');
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [uncertain, setUncertain] = useState([]);
  const [lastFile, setLastFile] = useState(null); // solo en memoria, para "Leer con IA"
  const pdfInput = useRef(null);

  // Ficha adjunta. PDF con texto ("Guardar como PDF"): se lee aquí mismo, sin enviarlo a ningún sitio.
  // PDF escaneado o captura de pantalla: no tienen texto, así que se leen con la IA.
  const readFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf && (!LEADS_AI_ENABLED || !AI_FILE_TYPES.includes(file.type))) { setFilled([]); setPasteMsg('Formato no admitido: usa un PDF o una captura (PNG o JPG).'); return; }
    if (file.size > (isPdf ? 15 : 8) * 1024 * 1024) { setFilled([]); setPasteMsg('El fichero es demasiado grande.'); return; }
    setLastFile(file); setUncertain([]);
    if (!isPdf) { runAi(file); return; }
    setPdfBusy(true); setPasteMsg('');
    try {
      const text = await extractPdfText(file);
      if (!text.trim()) {
        if (!LEADS_AI_ENABLED) { setFilled([]); setPasteMsg('Este PDF no tiene texto (parece un escaneo). Imprime la ficha con "Guardar como PDF".'); return; }
        setPasteMsg('Este PDF no tiene texto (parece un escaneo): leyéndolo con IA…');
        setPdfBusy(false);
        runAi(file);
        return;
      }
      applyPaste(text);
    } catch (err) {
      setFilled([]);
      setPasteMsg('No se ha podido leer el PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  // Lectura con IA (Edge Function extract-lead): el texto pegado o, si no hay, la ficha adjunta. Solo precarga.
  const runAi = async (fileArg) => {
    const file = fileArg || (paste.trim() ? null : lastFile);
    if (!file && !paste.trim()) return;
    if (file && file.size > AI_MAX_FILE) { setPasteMsg('Para leerlo con IA, el fichero debe ocupar menos de 8 MB.'); return; }
    setAiBusy(true); setPasteMsg(''); setFilled([]); setUncertain([]);
    const body = file
      ? { file: { media_type: file.type || 'application/pdf', data: await fileToBase64(file) } }
      : { text: paste };
    const { data, error: err } = await invokeFn(supabase, 'extract-lead', body);
    setAiBusy(false);
    if (err) { setPasteMsg(`IA: ${err}`); return; }
    const f = data?.fields || {};
    const keys = ['customer_name', 'phone', 'email', 'city', 'vehicle_text', 'vehicle_plate', 'vehicle_ref',
      'vehicle_price', 'vehicle_price_financed', 'customer_message', 'crm_lead_id'];
    const found = keys.filter(k => f[k] != null && String(f[k]).trim() !== '');
    if (found.length === 0 && !f.received_at) { setPasteMsg('La IA no ha encontrado datos del lead. Rellena los campos a mano.'); return; }
    setForm(prev => {
      const next = { ...prev };
      found.forEach(k => { next[k] = String(f[k]).replace(/[<>]/g, ''); });
      return next;
    });
    let msg = `🤖 Leído con IA: ${found.map(k => FIELD_NAMES[k] || k).join(', ')}.`;
    const recv = parseLocalDateTime(f.received_at);
    if (recv) {
      const diff = Date.now() - recv.getTime();
      if (diff >= -60000 && diff <= 7 * 86400000) { setReceivedAt(toLocalInput(recv)); found.push('received_at'); msg += ' Hora de entrada incluida.'; }
      else msg += ' La hora de entrada que ha leído no vale (futura o de hace más de 7 días): indícala a mano.';
    } else {
      msg += ' ⚠️ No ha encontrado una hora de entrada fiable: indícala a mano.';
    }
    const unsure = Array.isArray(f.fields_uncertain) ? f.fields_uncertain.filter(k => found.includes(k)) : [];
    if (unsure.length) msg += ` Revisa los marcados en rojo (dudosos): ${unsure.map(k => FIELD_NAMES[k] || k).join(', ')}.`;
    msg += ' Comprueba todo antes de guardar.';
    setFilled(found); setUncertain(unsure); setPasteMsg(msg);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const hl = (k) => (uncertain.includes(k)
    ? { borderColor: 'var(--t-f87171)', boxShadow: '0 0 0 1px var(--t-f87171)' }
    : filled.includes(k) ? { borderColor: 'var(--l-f59e0b)', boxShadow: '0 0 0 1px var(--l-f59e0b)' } : null);

  const stockVehicles = (vehicles || []).filter(v => v.plate);
  const match = form.vehicle_plate ? stockVehicles.find(v => normalizePlate(v.plate) === normalizePlate(form.vehicle_plate)) : null;
  const matchColumn = match ? (columns || []).find(c => c.id === match.column_id)?.label : null;

  const applyPaste = (text) => {
    const result = parseCrmText(text);
    const { fields, found } = result;
    if (found.length === 0) {
      setFilled([]);
      setPasteMsg('No he encontrado datos reconocibles. Rellena los campos a mano.');
      return;
    }
    const next = { ...form };
    found.forEach(k => {
      if (k === 'received_at') return;
      next[k] = (k === 'vehicle_price' || k === 'vehicle_price_financed') ? String(fields[k]) : fields[k];
    });
    setForm(next);
    let msg = `Rellenado: ${found.map(k => FIELD_NAMES[k] || k).join(', ')}. Revísalo antes de guardar.`;
    if (fields.received_at) {
      const diff = Date.now() - fields.received_at.getTime();
      if (diff >= -60000 && diff <= 7 * 86400000) setReceivedAt(toLocalInput(fields.received_at));
      else msg += ' La fecha de la ficha no vale (futura o de hace más de 7 días): revisa la hora de entrada.';
    }
    if (!fields.customer_name) msg += ' Falta el nombre del cliente.';
    (result.warnings || []).forEach(w => { msg += ` ⚠️ ${w}`; });
    setFilled(found); setUncertain([]);
    setPasteMsg(msg);
  };

  const useStockData = () => {
    if (!match) return;
    setForm(f => ({
      ...f,
      vehicle_text: [match.brand, match.model, match.year].filter(Boolean).join(' '),
      vehicle_price: match.price != null ? String(match.price) : f.vehicle_price
    }));
  };

  const submit = async (force) => {
    setError('');
    if (!form.customer_name.trim()) { setError('El nombre del cliente es obligatorio.'); return; }
    const recv = new Date(receivedAt);
    if (Number.isNaN(recv.getTime())) { setError(online ? 'Indica la hora de entrada en el CRM.' : channel === 'telefono' ? 'Indica la hora de la llamada.' : 'Indica la hora de llegada del cliente.'); return; }
    if (recv.getTime() > Date.now() + 60000) { setError('La hora de entrada no puede ser futura.'); return; }
    if (recv.getTime() < Date.now() - 7 * 86400000) { setError('La hora de entrada no puede ser de hace más de 7 días.'); return; }
    const price = priceToNumber(form.vehicle_price);
    const priceFinanced = priceToNumber(form.vehicle_price_financed);
    if (Number.isNaN(price) || Number.isNaN(priceFinanced)) { setError('El precio no es un número válido.'); return; }
    if (/[<>]/.test(Object.values(form).join(''))) { setError('No se permiten los caracteres < ni >.'); return; }

    setSaving(true);
    const { data, error: err } = await supabase.rpc('create_lead', {
      p_customer_name: form.customer_name.trim(),
      p_phone: form.phone || null, p_email: form.email || null, p_city: form.city || null,
      p_vehicle_text: form.vehicle_text || null, p_vehicle_plate: form.vehicle_plate || null,
      p_vehicle_ref: form.vehicle_ref || null, p_vehicle_price: price,
      p_customer_message: form.customer_message || null,
      p_crm_lead_id: (online && form.crm_lead_id) || null, p_crm_url: (online && form.crm_url) || null, p_notes: form.notes || null,
      p_received_at: recv.toISOString(), p_claim: claim, p_is_test: isTest, p_force: !!force,
      p_vehicle_price_financed: priceFinanced, p_channel: channel
    });
    setSaving(false);
    if (err) {
      const msg = rpcError(err);
      if (msg.startsWith('DUPLICADO')) { setDuplicate(msg.replace(/^DUPLICADO:\s*/, '')); return; }
      setError(msg);
      return;
    }
    setDuplicate('');
    onCreated(data?.id, claim);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <button type="button" onClick={onCancel} style={{ ...L.secondary, padding: '6px 10px' }}>← Volver</button>
        <h4 style={{ ...L.title, fontSize: '1.05rem' }}>➕ Nuevo lead</h4>
      </div>

      <div style={{ ...L.section, display: 'grid', gap: '8px' }}>
        <span style={L.label}>¿Cómo ha llegado el cliente?</span>
        <Chips value={channel} onChange={setChannel} options={[
          { key: 'presencial', label: '🏪 Presencial' },
          { key: 'telefono', label: '📞 Llamada a tienda' },
          ...(access.supervisor ? [{ key: 'online', label: '🌐 Online (del CRM)' }] : [])
        ]} />
        <span style={L.muted}>
          {online
            ? 'Solo si un lead online no ha entrado solo desde el CRM. Cuenta como online en las métricas.'
            : channel === 'telefono'
              ? 'Cliente que ha llamado por teléfono a la tienda. Los leads online entran solos desde el CRM.'
              : 'Cliente que ha venido a la tienda. Los leads online entran solos desde el CRM.'}
        </span>
      </div>

      {online && <div style={L.section}>
        <h5 style={L.sectionTitle}>📋 Rellenar con la ficha del CRM</h5>
        <p style={{ ...L.muted, margin: '0 0 8px' }}>
          <strong>PDF:</strong> en la ficha del lead del CRM, imprime (Ctrl+P) → "Guardar como PDF" y adjúntalo: se lee en tu dispositivo,
          sin enviarlo a ningún sitio. <strong>O texto:</strong> selecciona todo (Ctrl+A), copia y pégalo abajo.
          {LEADS_AI_ENABLED && <><strong> 🤖 Leer con IA</strong>: para escaneos, capturas de pantalla o si algo no se reconoce; la ficha se envía a la IA solo para leerla (no se guarda). </>}
          Revisa siempre los datos antes de guardar.
        </p>
        <input ref={pdfInput} id="lead-pdf" type="file" accept={LEADS_AI_ENABLED ? "application/pdf,.pdf,image/png,image/jpeg,image/webp" : "application/pdf,.pdf"} onChange={readFile} style={{ display: 'none' }} />
        <button type="button" style={{ ...L.button, marginBottom: '10px' }} disabled={pdfBusy || aiBusy} onClick={() => pdfInput.current && pdfInput.current.click()}>
          {pdfBusy ? 'Leyendo PDF…' : LEADS_AI_ENABLED ? '📄 Adjuntar PDF o captura de la ficha' : '📄 Adjuntar PDF de la ficha'}
        </button>
        <textarea
          id="lead-paste"
          aria-label="Texto de la ficha del CRM"
          style={{ ...L.textarea, minHeight: '70px' }}
          value={paste}
          onChange={e => setPaste(e.target.value)}
          onPaste={e => { const t = e.clipboardData.getData('text'); setTimeout(() => applyPaste(t), 0); }}
          placeholder="Pega aquí el texto de la ficha…"
        />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" style={L.secondary} disabled={!paste.trim()} onClick={() => applyPaste(paste)}>✨ Rellenar campos</button>
          {LEADS_AI_ENABLED && <button type="button" style={L.secondary} disabled={aiBusy || (!paste.trim() && !lastFile)} onClick={() => runAi()}
            title={paste.trim() ? 'Lee el texto pegado con IA' : 'Lee la ficha adjunta con IA'}>
            {aiBusy ? '🤖 Leyendo…' : '🤖 Leer con IA'}
          </button>}
          {pasteMsg && <span style={{ color: filled.length ? 'var(--t-86efac)' : 'var(--t-fbbf24)', fontSize: '0.82rem' }}>{pasteMsg}</span>}
        </div>
      </div>}

      <div style={L.grid2}>
        <Field label={online ? 'Hora de entrada en el CRM *' : channel === 'telefono' ? 'Hora de la llamada *' : 'Hora de llegada del cliente *'} htmlFor="lead-received" hint="De ella depende el tiempo de atención. Máximo 7 días atrás.">
          <input id="lead-received" type="datetime-local" style={{ ...L.input, ...hl('received_at') }} value={receivedAt} max={toLocalInput(new Date())} onChange={e => setReceivedAt(e.target.value)} />
        </Field>
        <Field label="Nombre del cliente *" htmlFor="lead-name">
          <input id="lead-name" style={{ ...L.input, ...hl('customer_name') }} value={form.customer_name} onChange={set('customer_name')} autoComplete="off" />
        </Field>
        <Field label="Teléfono" htmlFor="lead-phone">
          <input id="lead-phone" type="tel" inputMode="tel" style={{ ...L.input, ...hl('phone') }} value={form.phone} onChange={set('phone')} autoComplete="off" />
        </Field>
        <Field label="Email" htmlFor="lead-email">
          <input id="lead-email" type="email" style={{ ...L.input, ...hl('email') }} value={form.email} onChange={set('email')} autoComplete="off" />
        </Field>
        <Field label="Localidad" htmlFor="lead-city">
          <input id="lead-city" style={{ ...L.input, ...hl('city') }} value={form.city} onChange={set('city')} autoComplete="off" />
        </Field>
        <Field label="Coche de interés" htmlFor="lead-vehicle">
          <input id="lead-vehicle" style={{ ...L.input, ...hl('vehicle_text') }} value={form.vehicle_text} onChange={set('vehicle_text')} placeholder="Seat León 1.5 TSI 2020" autoComplete="off" />
        </Field>
        <Field label="Matrícula" htmlFor="lead-plate">
          <input id="lead-plate" list="lead-plates" style={{ ...L.input, ...hl('vehicle_plate') }} value={form.vehicle_plate} onChange={set('vehicle_plate')} autoComplete="off" />
          <datalist id="lead-plates">
            {stockVehicles.map(v => <option key={v.id} value={v.plate}>{v.brand} {v.model}</option>)}
          </datalist>
        </Field>
        <Field label="Enlace del anuncio en la web" htmlFor="lead-ref">
          <input id="lead-ref" type="url" style={{ ...L.input, ...hl('vehicle_ref') }} value={form.vehicle_ref} onChange={set('vehicle_ref')} placeholder="https://www.flexicar.es/…" autoComplete="off" />
        </Field>
        <Field label="Precio al contado (€)" htmlFor="lead-price">
          <input id="lead-price" inputMode="decimal" style={{ ...L.input, ...hl('vehicle_price') }} value={form.vehicle_price} onChange={set('vehicle_price')} autoComplete="off" />
        </Field>
        <Field label="Precio financiado (€)" htmlFor="lead-price-fin">
          <input id="lead-price-fin" inputMode="decimal" style={{ ...L.input, ...hl('vehicle_price_financed') }} value={form.vehicle_price_financed} onChange={set('vehicle_price_financed')} autoComplete="off" />
        </Field>
      </div>

      {match && (
        <div style={{ ...L.ok, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>🔗 Coche en EnMarcha: <strong>{match.brand} {match.model}</strong>{matchColumn ? ` · ${matchColumn}` : ''}. El lead quedará vinculado.</span>
          <button type="button" style={{ ...L.secondary, padding: '4px 10px', fontSize: '0.8rem' }} onClick={useStockData}>Usar sus datos</button>
        </div>
      )}

      <Field label="Mensaje del cliente" htmlFor="lead-message">
        <textarea id="lead-message" style={{ ...L.textarea, ...hl('customer_message') }} value={form.customer_message} onChange={set('customer_message')} />
      </Field>
      {online && <div style={L.grid2}>
        <Field label="Referencia del lead en el CRM" htmlFor="lead-crm-id">
          <input id="lead-crm-id" style={{ ...L.input, ...hl('crm_lead_id') }} value={form.crm_lead_id} onChange={set('crm_lead_id')} autoComplete="off" />
        </Field>
        <Field label="Enlace a la ficha del CRM" htmlFor="lead-crm-url">
          <input id="lead-crm-url" type="url" style={{ ...L.input, ...hl('crm_url') }} value={form.crm_url} onChange={set('crm_url')} autoComplete="off" />
        </Field>
      </div>}
      <Field label="Notas internas" htmlFor="lead-notes">
        <textarea id="lead-notes" style={{ ...L.textarea, minHeight: '60px' }} value={form.notes} onChange={set('notes')} />
      </Field>

      <div style={{ display: 'grid', gap: '8px', marginBottom: '14px' }}>
        {access.can_claim && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--t-cbd5e1)', fontSize: '0.9rem', cursor: 'pointer' }}>
            <input id="lead-claim" type="checkbox" checked={claim} onChange={e => setClaim(e.target.checked)} />
            Quedármelo (no pasa por la Bandeja ni avisa al resto)
          </label>
        )}
        {LEADS_TEST_MODE && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--t-cbd5e1)', fontSize: '0.9rem', cursor: 'pointer' }}>
            <input id="lead-test" type="checkbox" checked={isTest} onChange={e => setIsTest(e.target.checked)} />
            Es de prueba (no cuenta en las métricas)
          </label>
        )}
      </div>

      {duplicate && (
        <div style={L.warn}>
          ⚠️ <strong>Posible duplicado:</strong> {duplicate}.
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button type="button" style={{ ...L.secondary, padding: '5px 10px' }} onClick={() => setDuplicate('')}>Revisar</button>
            <button type="button" style={{ ...L.button, padding: '5px 12px' }} disabled={saving} onClick={() => submit(true)}>Registrar igualmente</button>
          </div>
        </div>
      )}
      {error && <div style={L.error}>{error}</div>}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button type="button" style={L.secondary} onClick={onCancel}>Cancelar</button>
        <button type="button" style={L.button} disabled={saving} onClick={() => submit(false)}>{saving ? 'Guardando…' : 'Registrar lead'}</button>
      </div>
    </div>
  );
}
